// ═══════════════════════════════════════════════════════════════
// AIImportModal — Classification IA de fichiers en chapitres (S57 D)
// Flux : sélection fichiers → propose → review → apply
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useState } from 'react';
import { X, FileText, Sparkles, Loader, Check, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { aiImportPropose, aiImportApply } from '../../services/bookApi';
import { getTerms } from '../../utils/documentTerminology';

const AIImportModal = ({ bookId, projectId, book, onClose, onCompleted }) => {
    const [step, setStep] = useState('select'); // select | proposing | review | applying | done
    const [files, setFiles] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [hint, setHint] = useState('');
    const [proposal, setProposal] = useState(null);
    const [error, setError] = useState('');
    const t = getTerms(book?.genre);
    const unitLower = t.unitSingular.toLowerCase();
    const unitsLower = t.unitPlural.toLowerCase();

    useEffect(() => {
        (async () => {
            try {
                const token = localStorage.getItem('oracle_token');
                const res = await fetch(`/api/projects/${projectId}/files`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                });
                const data = await res.json();
                setFiles(data.files || data || []);
            } catch {
                setFiles([]);
            }
        })();
    }, [projectId]);

    const toggleFile = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };
    const selectAll = () => setSelectedIds(files.map(f => f.id));
    const selectNone = () => setSelectedIds([]);

    const handlePropose = async () => {
        if (selectedIds.length === 0) { setError('Sélectionne au moins un fichier.'); return; }
        setError('');
        setStep('proposing');
        try {
            const res = await aiImportPropose(bookId, selectedIds, { hint: hint.trim() });
            setProposal(res);
            setStep('review');
        } catch (err) {
            setError(err.detail?.message || err.message || 'Erreur analyse IA');
            setStep('select');
        }
    };

    const updateChapter = (index, patch) => {
        setProposal(p => ({
            ...p,
            mapping: p.mapping.map((c, i) => i === index ? { ...c, ...patch } : c),
        }));
    };
    const removeChapter = (index) => {
        setProposal(p => ({
            ...p,
            mapping: p.mapping.filter((_, i) => i !== index).map((c, i) => ({ ...c, position: i + 1 })),
        }));
    };
    const moveChapter = (index, direction) => {
        const target = direction === 'up' ? index - 1 : index + 1;
        if (target < 0 || target >= proposal.mapping.length) return;
        setProposal(p => {
            const next = [...p.mapping];
            [next[index], next[target]] = [next[target], next[index]];
            next.forEach((c, i) => { c.position = i + 1; });
            return { ...p, mapping: next };
        });
    };

    const handleApply = async () => {
        if (!proposal || proposal.mapping.length === 0) return;
        setStep('applying');
        try {
            const res = await aiImportApply(bookId, proposal.mapping);
            setStep('done');
            if (onCompleted) onCompleted(res);
        } catch (err) {
            setError(err.detail?.message || err.message || 'Erreur création chapitres');
            setStep('review');
        }
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 100,
            background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(2px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} style={{
                width: 780, maxWidth: '94vw', maxHeight: '86vh', overflow: 'auto',
                background: 'var(--card-bg)',
                border: '1px solid var(--card-border)',
                borderRadius: 12, padding: '24px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Sparkles size={20} style={{ color: '#a78bfa' }} />
                        <div>
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>
                                Importer & classer en {unitsLower} (IA)
                            </h3>
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3 }}>
                                {step === 'select' && `Sélectionne les fichiers à organiser en ${unitsLower}.`}
                                {step === 'proposing' && 'Analyse IA en cours…'}
                                {step === 'review' && 'Propose-toi de valider ou ajuster la structure avant création.'}
                                {step === 'applying' && `Création des ${unitsLower}…`}
                                {step === 'done' && `✓ ${t.unitPlural} créés avec succès.`}
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                        <X size={20} />
                    </button>
                </div>

                {step === 'select' && (
                    <>
                        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                {files.length} fichier(s) dans le projet · {selectedIds.length} sélectionné(s)
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <button onClick={selectAll} style={linkBtn}>Tout</button>
                                <button onClick={selectNone} style={linkBtn}>Aucun</button>
                            </div>
                        </div>
                        <div style={{ maxHeight: 280, overflow: 'auto', border: '1px solid var(--card-border)', borderRadius: 8, marginBottom: 12 }}>
                            {files.length === 0 && (
                                <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                                    Aucun fichier dans ce projet. Uploade d'abord des fichiers depuis l'onglet Fichiers.
                                </div>
                            )}
                            {files.map(f => (
                                <label key={f.id} style={{
                                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                                    cursor: 'pointer',
                                    background: selectedIds.includes(f.id) ? 'rgba(139,92,246,0.08)' : 'transparent',
                                }}>
                                    <input type="checkbox" checked={selectedIds.includes(f.id)} onChange={() => toggleFile(f.id)} />
                                    <FileText size={14} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <div style={{ fontSize: 12, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {f.original_name || f.name || `#${f.id}`}
                                        </div>
                                        <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                                            {f.file_size ? `${Math.round(f.file_size / 1024)} KB` : ''} {f.extraction_status === 'complete' ? '· extrait' : ''}
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Indication (optionnel)</label>
                            <textarea
                                value={hint}
                                onChange={(e) => setHint(e.target.value)}
                                placeholder="Ex : 'organiser selon une progression chronologique', 'un chapitre par thème principal', etc."
                                rows={2}
                                style={{
                                    width: '100%', marginTop: 6, padding: '8px 12px', borderRadius: 6,
                                    background: 'var(--card-bg-secondary)', color: 'var(--text)', fontSize: 12, resize: 'vertical',
                                    border: '1px solid var(--card-border)',
                                }}
                            />
                        </div>
                        {error && <div style={errBox}>{error}</div>}
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button onClick={onClose} style={secondaryBtn}>Annuler</button>
                            <button onClick={handlePropose} disabled={selectedIds.length === 0} style={primaryBtn}>
                                <Sparkles size={12} /> Analyser & proposer
                            </button>
                        </div>
                    </>
                )}

                {step === 'proposing' && (
                    <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
                        <Loader size={28} className="animate-spin" style={{ color: '#a78bfa', marginBottom: 12 }} />
                        <div style={{ fontSize: 13 }}>Analyse du corpus par l'IA…</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                            Cela prend typiquement 15-45 secondes selon la taille des fichiers.
                        </div>
                    </div>
                )}

                {step === 'review' && proposal && (
                    <>
                        <div style={{ padding: 12, marginBottom: 14, borderRadius: 8, background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)' }}>
                            <div style={{ fontSize: 12, color: 'var(--text)', fontWeight: 500, marginBottom: 4 }}>
                                Proposition de structure — {proposal.mapping.length} {proposal.mapping.length > 1 ? unitsLower : unitLower}
                            </div>
                            {proposal.summary && <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{proposal.summary}</div>}
                            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 6 }}>
                                Modèle : {proposal.model_used}
                                {proposal.cost_usd != null && ` · coût $${Number(proposal.cost_usd).toFixed(4)}`}
                                {proposal.truncated && ' · ⚠ contenu tronqué pour tenir dans le budget'}
                            </div>
                        </div>
                        <div style={{ maxHeight: 380, overflow: 'auto', marginBottom: 14 }}>
                            {proposal.mapping.map((c, idx) => (
                                <div key={idx} style={{
                                    padding: 12, marginBottom: 8, borderRadius: 8,
                                    background: 'rgba(255,255,255,0.03)',
                                    border: '1px solid rgba(255,255,255,0.06)',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                        <div style={{ width: 24, height: 24, borderRadius: 4, background: 'rgba(139,92,246,0.15)', color: '#a78bfa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600 }}>
                                            {c.position}
                                        </div>
                                        <input
                                            value={c.title}
                                            onChange={(e) => updateChapter(idx, { title: e.target.value })}
                                            style={{
                                                flex: 1, padding: '6px 10px', borderRadius: 6, fontSize: 13, fontWeight: 500,
                                                background: 'var(--card-bg-secondary)', color: 'var(--text)',
                                                border: '1px solid var(--card-border)',
                                            }}
                                        />
                                        <button onClick={() => moveChapter(idx, 'up')} disabled={idx === 0} style={iconBtn(idx === 0)}><ChevronUp size={12} /></button>
                                        <button onClick={() => moveChapter(idx, 'down')} disabled={idx === proposal.mapping.length - 1} style={iconBtn(idx === proposal.mapping.length - 1)}><ChevronDown size={12} /></button>
                                        <button onClick={() => removeChapter(idx)} style={{ ...iconBtn(false), color: '#ef4444' }}><Trash2 size={12} /></button>
                                    </div>
                                    {c.summary && (
                                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.5, marginLeft: 32 }}>
                                            {c.summary}
                                        </div>
                                    )}
                                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4, marginLeft: 32 }}>
                                        Sources : {(c.sources || []).map(s => `#${s.file_id} (${s.length}c)`).join(' · ') || 'aucune'}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {error && <div style={errBox}>{error}</div>}
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button onClick={() => setStep('select')} style={secondaryBtn}>← Retour</button>
                            <button onClick={handleApply} disabled={proposal.mapping.length === 0} style={primaryBtn}>
                                <Check size={12} /> Créer {proposal.mapping.length} {proposal.mapping.length > 1 ? unitsLower : unitLower}
                            </button>
                        </div>
                    </>
                )}

                {step === 'applying' && (
                    <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
                        <Loader size={28} className="animate-spin" style={{ color: '#a78bfa', marginBottom: 12 }} />
                        <div style={{ fontSize: 13 }}>Création des {unitsLower}…</div>
                    </div>
                )}

                {step === 'done' && (
                    <div style={{ padding: 30, textAlign: 'center' }}>
                        <Check size={32} style={{ color: '#10b981', marginBottom: 12 }} />
                        <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 500 }}>{t.unitPlural} créés avec succès.</div>
                        <button onClick={onClose} style={{ ...primaryBtn, marginTop: 16 }}>Fermer</button>
                    </div>
                )}
            </div>
        </div>
    );
};

const primaryBtn = {
    padding: '8px 18px', borderRadius: 6, fontSize: 12, fontWeight: 500,
    background: 'linear-gradient(180deg, rgba(139,92,246,0.25), rgba(139,92,246,0.15))',
    border: '1px solid rgba(139,92,246,0.4)', color: '#c4b5fd',
    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
};
const secondaryBtn = {
    padding: '8px 18px', borderRadius: 6, fontSize: 12,
    background: 'transparent', color: 'var(--text-secondary)',
    border: '1px solid var(--card-border)', cursor: 'pointer',
};
const linkBtn = {
    fontSize: 11, padding: '3px 8px', borderRadius: 4,
    background: 'rgba(255,255,255,0.04)', border: '1px solid transparent',
    color: 'var(--text-secondary)', cursor: 'pointer',
};
const iconBtn = (disabled) => ({
    padding: 4, borderRadius: 4, background: 'rgba(255,255,255,0.04)',
    border: 'none', color: disabled ? 'var(--text-muted)' : 'var(--text-secondary)',
    cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex',
});
const errBox = {
    padding: 10, marginBottom: 12, borderRadius: 6,
    background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
    color: '#ef4444', fontSize: 12,
};

export default AIImportModal;
